import React, { useState, useMemo } from 'react';
import { Row, Col, FormGroup, Label, Input, Button, Table } from 'reactstrap';
import Select from 'components/Select';
import PercentageInput from '../PercentageInput';
import { useUpdateSalesLocation } from '../../api/sales-locations/service';
import { toNumber } from 'utils/currency';
import { getActiveColorStyle } from 'utils/colorUtils';
import { getTheme2FormPageDarkSelectStyles } from 'utils/theme2MultiSelect';

const BuyersGuide = ({ formData, activeColor = "blue", isTheme2 = false, salesLocationId, onSave }) => {
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const selectStyles = useMemo(
        () => getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );
    // Safety check - if formData is undefined, provide default values
    const safeFormData = formData || {};
    const updateSalesLocationMutation = useUpdateSalesLocation();

    // Local state for form data
    const [localFormData, setLocalFormData] = React.useState(formData || {});

    // Systems covered state - always keep at least one empty row
    const [systemsCovered, setSystemsCovered] = useState(safeFormData.systems_covered?.length > 0 ? safeFormData.systems_covered : [{ systems_covered: '', duration: '' }]);

    // Update local state when formData prop changes
    React.useEffect(() => {
        setLocalFormData(formData || {});
    }, [formData]);

    const saveBuyersGuideRef = React.useRef();

    // Function to save buyers guide data
    const saveBuyersGuide = React.useCallback(() => {
        if (salesLocationId && localFormData) {
            const updateData = {
                buyers_guide_defaults: {
                    ...localFormData,
                    percentage_of_labor: toNumber(localFormData.percentage_of_labor) || 0,
                    percentage_of_parts: toNumber(localFormData.percentage_of_parts) || 0,
                    systems_covered: systemsCovered
                }
            };

            updateSalesLocationMutation.mutate({
                id: salesLocationId,
                data: updateData
            });
        }
    }, [salesLocationId, localFormData, systemsCovered, updateSalesLocationMutation]);

    // Update ref whenever saveBuyersGuide changes
    React.useEffect(() => {
        saveBuyersGuideRef.current = saveBuyersGuide;
    }, [saveBuyersGuide]);

    // Call onSave callback when component mounts to register save function
    React.useEffect(() => {
        if (onSave) {
            onSave('BUYERS_GUIDE_DEFAULTS', () => saveBuyersGuideRef.current?.());
        }
    }, [onSave]);

    // Add new system covered
    const addSystemCovered = () => {
        setSystemsCovered(prev => [...prev, { systems_covered: '', duration: '' }]);
    };

    // Update system covered
    const updateSystemCovered = (index, field, value) => {
        setSystemsCovered(prev => prev.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    };

    // Delete system covered - but always keep at least one row
    const deleteSystemCovered = (index) => {
        setSystemsCovered(prev => {
            if (prev.length > 1) {
                return prev.filter((_, i) => i !== index);
            }
            return prev; // Don't delete if it's the last row
        });
    };
    return (
        <div className="">
            <h5 className="mb-3">BUYERS GUIDE DEFAULTS</h5>

            <Row>
                <Col md="4">
                    <FormGroup>
                        <Label className='form-control-label'>Buyers Guide Option:</Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            value={localFormData.buyers_guide_option ? { value: localFormData.buyers_guide_option, label: localFormData.buyers_guide_option } : null}
                            onChange={(option) => {
                                const value = option?.value || '';
                                setLocalFormData(prev => ({ ...prev, buyers_guide_option: value }));
                            }}
                            options={[
                                { value: 'AS-IS', label: 'AS-IS' },
                                { value: 'WARRANTY', label: 'WARRANTY' },
                                { value: 'SERVICE CONTRACT', label: 'SERVICE CONTRACT' }
                            ]}
                        />
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label className='form-control-label'>Is Service Contract Available:</Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            value={localFormData.is_service_contract_available ? { value: localFormData.is_service_contract_available, label: localFormData.is_service_contract_available } : null}
                            onChange={(option) => {
                                const value = option?.value || '';
                                setLocalFormData(prev => ({ ...prev, is_service_contract_available: value }));
                            }}
                            options={[
                                { value: 'yes', label: 'Yes' },
                                { value: 'no', label: 'No' }
                            ]}
                        />
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label className='form-control-label'>Warranty Type:</Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            value={localFormData.warranty_type ? { value: localFormData.warranty_type, label: localFormData.warranty_type } : null}
                            onChange={(option) => {
                                const value = option?.value || '';
                                setLocalFormData(prev => ({ ...prev, warranty_type: value }));
                            }}
                            options={[
                                { value: 'Full Warranty', label: 'Full Warranty' },
                                { value: 'Limited Warranty', label: 'Limited Warranty' },
                            ]}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label className='form-control-label'>Percentage of Labor:</Label>
                        <PercentageInput
                            value={localFormData.percentage_of_labor}
                            onChange={(value) => {
                                setLocalFormData(prev => ({ ...prev, percentage_of_labor: value }));
                            }}
                            decimals={2}
                            maxValue={100}
                        />
                    </FormGroup>
                </Col>
                <Col md="6">
                    <FormGroup>
                        <Label className='form-control-label'>Percentage of Parts:</Label>
                        <PercentageInput
                            value={localFormData.percentage_of_parts}
                            onChange={(value) => {
                                setLocalFormData(prev => ({ ...prev, percentage_of_parts: value }));
                            }}
                            decimals={2}
                            maxValue={100}
                        />
                    </FormGroup>
                </Col>
            </Row>

            {/* Systems Covered Section */}
            <div >

                <Table >
                    <thead className="text-primary">
                        <tr>
                            <th style={{ width: '47.5%' }}>Systems Covered</th>
                            <th style={{ width: '47.5%' }}>Duration</th>
                            {systemsCovered.length > 1 && (
                                <th className="text-right" style={{ width: '5%' }}>Actions</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {systemsCovered.map((system, index) => (
                            <tr key={index}>
                                <td>
                                    <Input
                                        type="text"
                                        value={system.systems_covered}
                                        onChange={(e) => updateSystemCovered(index, 'systems_covered', e.target.value)}
                                        placeholder="Enter systems covered"
                                        style={{ borderRadius: 0, width: '100%' }}
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="text"
                                        value={system.duration}
                                        onChange={(e) => updateSystemCovered(index, 'duration', e.target.value)}
                                        placeholder="Enter duration"
                                        style={{ borderRadius: 0, width: '100%' }}
                                    />
                                </td>
                                {systemsCovered.length > 1 && (
                                    <td className="text-right">
                                        <Button
                                            color="danger"
                                            size="sm"
                                            className="btn-icon btn-link like"
                                            onClick={() => deleteSystemCovered(index)}
                                        >
                                            <i className="tim-icons icon-simple-remove" />
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
                <div className="d-flex justify-content-end align-items-center mb-3">
                    <Button
                        size="sm"
                        color={activeColor}
                        onClick={addSystemCovered}
                    >
                        <i className="tim-icons icon-simple-add mr-1" />
                        Add System
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default BuyersGuide;
